import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { IUser } from '@shared/common/interfaces';

import {
    LoadMembers,
    LoadMembersSuccess,
    LoadMembersError,
    ClearMembersForChannel,
} from '../actions/member.actions';

export interface MemberStateModel {
    byChannel: {
        [channelId: string]: {
            items: IUser[];
            loading: boolean;
            error: unknown;
            lastUpdated: number;
        };
    };
}

@State<MemberStateModel>({
    name: 'members',
    defaults: {
        byChannel: {},
    },
})
@Injectable()
export class MemberState {
    constructor(private chatService: ChatService) {}

    @Selector()
    static getMembers(state: MemberStateModel | undefined) {
        return (channelId: string): IUser[] => state?.byChannel?.[channelId]?.items ?? [];
    }

    @Selector()
    static isLoading(state: MemberStateModel | undefined) {
        return (channelId: string): boolean => !!state?.byChannel?.[channelId]?.loading;
    }

    @Action(LoadMembers)
    loadMembers(ctx: StateContext<MemberStateModel>, action: LoadMembers) {
        this.ensureChannel(ctx, action.channelId);
        this.patchChannel(ctx, action.channelId, { loading: true, error: null });

        // Create requests for each member ID
        const requests = action.memberIds.map((memberId) =>
            this.chatService.getUserDetails(memberId),
        );

        return forkJoin(requests).pipe(
            tap((members: IUser[]) =>
                ctx.dispatch(new LoadMembersSuccess(action.channelId, members)),
            ),
            catchError((error) => {
                ctx.dispatch(new LoadMembersError(action.channelId, error));
                return of(error);
            }),
        );
    }

    @Action(LoadMembersSuccess)
    loadMembersSuccess(ctx: StateContext<MemberStateModel>, action: LoadMembersSuccess) {
        this.patchChannel(ctx, action.channelId, {
            items: action.members,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadMembersError)
    loadMembersError(ctx: StateContext<MemberStateModel>, action: LoadMembersError) {
        this.patchChannel(ctx, action.channelId, { loading: false, error: action.error });
    }

    @Action(ClearMembersForChannel)
    clearMembersForChannel(ctx: StateContext<MemberStateModel>, action: ClearMembersForChannel) {
        const state = ctx.getState();
        const { [action.channelId]: _removed, ...rest } = state.byChannel;
        ctx.patchState({ byChannel: rest });
    }

    // Helpers
    private ensureChannel(ctx: StateContext<MemberStateModel>, channelId: string) {
        const state = ctx.getState();
        if (!state.byChannel[channelId]) {
            ctx.patchState({
                byChannel: {
                    ...state.byChannel,
                    [channelId]: {
                        items: [],
                        loading: false,
                        error: null,
                        lastUpdated: 0,
                    },
                },
            });
        }
    }

    private patchChannel(
        ctx: StateContext<MemberStateModel>,
        channelId: string,
        patch: Partial<MemberStateModel['byChannel'][string]>,
    ) {
        const state = ctx.getState();
        const slice = state.byChannel[channelId] ?? {
            items: [],
            loading: false,
            error: null,
            lastUpdated: 0,
        };
        ctx.patchState({
            byChannel: {
                ...state.byChannel,
                [channelId]: { ...slice, ...patch },
            },
        });
    }
}
